import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Minus, Plus, User, Users } from "lucide-react";
import { hackathon, feeFor, registrationTotal, rupees } from "../../data/hackathon";
import { EASE } from "./hk";
import { loadRazorpay, postJson } from "./razorpay";
import "./RegistrationFlow.scss";

const { registration: reg, eligibility, form: OPTIONS } = hackathon;
const { min: MIN_TEAM, max: MAX_TEAM } = reg.team;

// sessionStorage, not localStorage: school labs share computers, so a draft
// (student contact details) must not outlive the browser tab.
const DRAFT_KEY = "yuvasetu-registration-draft";

const blankMember = () => ({ name: "", studentClass: "", email: "", phone: "" });

const EMPTY = {
  mode: "",
  size: MIN_TEAM,
  school: "", city: "", state: "", board: "",
  teamName: "",
  members: [blankMember()],
  mentorName: "", mentorPhone: "",
  guardianName: "", guardianPhone: "",
  method: reg.methods[0],
  consent: false,
  website: "",
};

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isPhone = (d) => /^[6-9]\d{9}$/.test(d);

/**
 * Mobile digits without a 0 / +91 prefix. An 11-digit "91…" is kept while the
 * user is still typing out the country code; the 12th digit strips it.
 */
function phoneDigits(v) {
  let d = String(v).replace(/\D/g, "").replace(/^0+/, "");
  if (d.length >= 12 && d.startsWith("91")) d = d.slice(2);
  return d.slice(0, d.length === 11 && d.startsWith("91") ? 11 : 10);
}
const fmtPhone = (d) => (d.length > 5 ? `${d.slice(0, 5)} ${d.slice(5)}` : d);

const DOMAIN_FIX = {
  "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gamil.com": "gmail.com", "gnail.com": "gmail.com",
  "gmail.co": "gmail.com", "gmail.con": "gmail.com", "gmail.cm": "gmail.com", "gmail.in": "gmail.com",
  "yaho.com": "yahoo.com", "yahoo.co": "yahoo.com", "yahoo.con": "yahoo.com",
  "hotmial.com": "hotmail.com", "hotmail.co": "hotmail.com", "outlok.com": "outlook.com",
  "rediffmail.co": "rediffmail.com", "redifmail.com": "rediffmail.com",
};
function emailSuggestion(v) {
  const [user, domain] = v.trim().toLowerCase().split("@");
  const fix = user && DOMAIN_FIX[domain];
  return fix ? `${user}@${fix}` : null;
}

function phoneError(d, required) {
  if (!d) return required ? "Enter a 10-digit mobile number." : "";
  if (d.length < 10) return `Enter all 10 digits (${d.length} so far).`;
  if (d.length > 10) return "Enter the 10-digit number without +91.";
  return isPhone(d) ? "" : "Indian mobile numbers start with 6, 7, 8 or 9.";
}

const NAME_FIELDS = ["school", "city", "teamName", "guardianName", "mentorName"];
const titleCase = (v) =>
  v === v.toLowerCase() ? v.replace(/(^|[\s.-])(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase()) : v;

const payingCount = (f) => (f.mode === "team" ? f.size : 1);
const leadLabel = (f) => (f.mode === "team" ? "Team lead" : "Student");

// ─── Validation ───────────────────────────────────────────────────────────────
// One rule table per step, so the step bar, the Continue button and the review
// screen all agree on what "complete" means.

function memberErrors(m, i, f) {
  const e = {};
  if (!m.name.trim()) e.name = `Enter ${i === 0 ? "your" : `member ${i + 1}'s`} full name.`;
  if (!m.studentClass) e.studentClass = "Choose a class.";
  if (!m.email.trim()) e.email = "Enter an email address.";
  else if (!isEmail(m.email)) e.email = "That email doesn't look right. Check for typos.";
  // Only the lead's phone is required — it is the number the team is contacted on.
  if (i === 0) {
    const err = phoneError(m.phone, true);
    if (err) e.phone = err;
  }
  return e;
}

function stepErrors(step, f) {
  const e = {};
  if (step === 0) {
    if (!f.mode) e.mode = "Choose how you're registering.";
  }
  if (step === 1) {
    if (!f.school.trim()) e.school = "Enter the school name.";
    if (!f.city.trim()) e.city = "Enter the city.";
    if (!f.state) e.state = "Choose a state.";
    if (!f.board) e.board = "Choose a board.";
  }
  if (step === 2) {
    if (f.mode === "team" && !f.teamName.trim()) e.teamName = "Give your team a name.";
    f.members.forEach((m, i) => {
      const me = memberErrors(m, i, f);
      if (Object.keys(me).length) e[`member${i}`] = me;
    });
    if (f.mode === "individual" && !f.guardianName.trim()) {
      e.guardianName = "Enter a parent or guardian's name.";
    }
    const gp = phoneError(f.guardianPhone, false);
    if (gp) e.guardianPhone = gp;
    const mp = phoneError(f.mentorPhone, false);
    if (mp) e.mentorPhone = mp;
  }
  if (step === 3) {
    if (!f.consent) e.consent = "Parent or guardian consent is required.";
  }
  return e;
}

const stepValid = (step, f) => Object.keys(stepErrors(step, f)).length === 0;

const STEPS = [
  { key: "type",    title: "Type",    heading: "How are you registering?" },
  { key: "school",  title: "School",  heading: "School details" },
  { key: "members", title: "Members", heading: "Who is taking part?" },
  { key: "pay",     title: "Pay",     heading: "Review and pay" },
];
const LAST = STEPS.length - 1;

// ─── Draft ────────────────────────────────────────────────────────────────────

function loadDraft() {
  try {
    const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    if (!d || typeof d.f !== "object" || !d.f.mode) return null;
    return { f: { ...EMPTY, ...d.f, consent: false, website: "" }, step: Math.min(Number(d.step) || 0, LAST) };
  } catch {
    return null;
  }
}
function saveDraft(f, step) {
  try {
    const { consent, website, ...rest } = f;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ f: rest, step }));
  } catch { /* storage unavailable; drafts are a convenience */ }
}
function clearDraft() {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
}

// ─── Small pieces ─────────────────────────────────────────────────────────────

function Field({ id, label, hint, error, optional, children }) {
  return (
    <div className={`rf-field${error ? " has-error" : ""}`}>
      <label htmlFor={id} className="rf-field__label">
        {label}
        {optional
          ? <span className="rf-field__opt">Optional</span>
          : <span className="rf-field__req" aria-hidden="true">*</span>}
      </label>
      {children}
      {error ? (
        <motion.p
          id={`${id}-err`}
          className="rf-field__err"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18 }}
        >
          {error}
        </motion.p>
      ) : hint ? (
        <p className="rf-field__hint">{hint}</p>
      ) : null}
    </div>
  );
}

function ClassPicker({ id, value, onChange, error }) {
  return (
    <div id={id} className="rf-classes" role="radiogroup" aria-label="Class" aria-describedby={error ? `${id}-err` : undefined}>
      {eligibility.classList.map((c) => (
        <label key={c} className={`rf-class${value === c ? " is-on" : ""}`}>
          <input type="radio" name={id} value={c} checked={value === c} onChange={() => onChange(c)} />
          <span>{c}</span>
        </label>
      ))}
    </div>
  );
}

/** The paper receipt — the running total, visible at every step. */
function Summary({ f, compact }) {
  const n = payingCount(f);
  const total = registrationTotal(f.mode, n);

  return (
    <aside className={`rf-receipt${compact ? " is-compact" : ""}`} aria-label="Order summary">
      <p className="rf-receipt__label">Your registration</p>

      <dl className="rf-receipt__meta">
        <div>
          <dt>Type</dt>
          <dd>{f.mode === "team" ? `Team of ${f.size}` : f.mode === "individual" ? "Individual" : "—"}</dd>
        </div>
        {f.mode === "team" && f.teamName.trim() && (
          <div>
            <dt>Team</dt>
            <dd>{f.teamName.trim()}</dd>
          </div>
        )}
        {f.school.trim() && (
          <div>
            <dt>School</dt>
            <dd>{f.school.trim()}</dd>
          </div>
        )}
      </dl>

      {!f.mode && (
        <p className="rf-receipt__empty">
          Choose individual or team and the total will appear here.
        </p>
      )}

      <ul className="rf-receipt__lines">
        {f.mode && f.members.slice(0, n).map((m, i) => (
          <li key={i}>
            <span className="rf-receipt__who">
              {m.name.trim() || `${i === 0 ? leadLabel(f) : `Member ${i + 1}`}`}
            </span>
            <span className="rf-receipt__amt">{rupees(feeFor(f.mode))}</span>
          </li>
        ))}
        {f.mentorName.trim() && (
          <li className="is-free">
            <span className="rf-receipt__who">{f.mentorName.trim()} · mentor</span>
            <span className="rf-receipt__amt">Free</span>
          </li>
        )}
      </ul>

      {f.mode && (
        <>
          <p className="rf-receipt__total">
            <span>Total</span>
            <b>{rupees(total)}</b>
          </p>
          <p className="rf-receipt__fine">
            {rupees(feeFor(f.mode))} {reg.feeUnit} × {n}. A mentor teacher is not charged.
          </p>
        </>
      )}
    </aside>
  );
}

// ─── Flow ─────────────────────────────────────────────────────────────────────

export default function RegistrationFlow({ onClose, autoFocus = true, rail = true }) {
  const [init] = useState(() => loadDraft());
  const [f, setF] = useState(() => (init ? init.f : EMPTY));
  const [step, setStep] = useState(() => (init ? init.step : 0));
  const [dir, setDir] = useState(1);
  const [shown, setShown] = useState(() => (init ? new Set(STEPS.map((_, i) => i)) : new Set()));
  const [paying, setPaying] = useState(false);
  const [handoff, setHandoff] = useState(null);
  const [payError, setPayError] = useState("");
  const headingRef = useRef(null);
  const bodyRef = useRef(null);

  const errors = useMemo(() => stepErrors(step, f), [step, f]);
  const touched = shown.has(step);
  const show = (k) => (touched ? errors[k] : undefined);

  useEffect(() => { saveDraft(f, step); }, [f, step]);

  useEffect(() => {
    if (!autoFocus) return;
    headingRef.current?.focus({ preventScroll: true });
    bodyRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
  }, [step, autoFocus]);

  const set = useCallback((k, v) => setF((p) => ({ ...p, [k]: v })), []);

  const setMember = useCallback((i, k, v) => {
    setF((p) => {
      const members = p.members.map((m, j) => (j === i ? { ...m, [k]: v } : m));
      return { ...p, members };
    });
  }, []);

  // The member list always matches the chosen team size, so the receipt and the
  // total can never disagree with the number of name fields on screen.
  const setSize = useCallback((size) => {
    setF((p) => {
      const members = [...p.members];
      while (members.length < size) members.push(blankMember());
      return { ...p, size, members: members.slice(0, Math.max(size, 1)) };
    });
  }, []);

  const setMode = useCallback((mode) => {
    setF((p) => {
      const size = mode === "team" ? Math.max(p.size, MIN_TEAM) : 1;
      const members = [...p.members];
      while (members.length < size) members.push(blankMember());
      return { ...p, mode, size: mode === "team" ? size : MIN_TEAM, members: members.slice(0, size) };
    });
  }, []);

  const go = (to) => {
    setDir(to > step ? 1 : -1);
    setStep(to);
  };

  const next = () => {
    setShown((s) => new Set(s).add(step));
    if (!stepValid(step, f)) return;
    if (step < LAST) go(step + 1);
  };

  const back = () => { if (step > 0) go(step - 1); };

  const pay = async () => {
    setShown((s) => new Set(s).add(LAST));
    if (!stepValid(LAST, f)) return;
    if (f.website) return; // honeypot: bots fill this, real users never see it

    setPayError("");
    setPaying(true);

    try {
      // The server prices the registration and opens the order. The amount
      // below is only ever displayed — it is never sent, so it cannot be edited.
      const [Razorpay, order] = await Promise.all([
        loadRazorpay(),
        postJson("/api/hackathon/order", {
          mode: f.mode,
          size: payingCount(f),
          school: f.school, city: f.city, state: f.state, board: f.board,
          teamName: f.teamName,
          members: f.members.slice(0, payingCount(f)),
          mentorName: f.mentorName, mentorPhone: f.mentorPhone,
          guardianName: f.guardianName, guardianPhone: f.guardianPhone,
          method: f.method,
          consent: f.consent,
        }),
      ]);

      const lead = f.members[0] || {};
      const rzp = new Razorpay({
        key: order.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: `${hackathon.eventName} ${hackathon.title.match(/\d{4}/)?.[0] || ""}`.trim(),
        description:
          order.students === 1 ? "Registration · 1 student" : `Registration · ${order.students} students`,
        image: `${window.location.origin}/logo192.png`,
        prefill: { name: lead.name || "", email: lead.email || "", contact: lead.phone || "" },
        notes: { school: f.school },
        theme: { color: "#f97316" },
        retry: { enabled: false },
        modal: {
          // Closing the sheet is not a failure — the order stays open so the
          // student can press Pay again without re-entering anything.
          ondismiss: () => {
            setPaying(false);
            setPayError("Payment was cancelled. Nothing has been charged — press Pay to try again.");
          },
        },
        handler: async (resp) => {
          try {
            const ok = await postJson("/api/hackathon/verify", resp);
            clearDraft();
            setHandoff({
              amount: ok.amount / 100,
              students: ok.students,
              paymentId: ok.paymentId,
              registrationId: ok.registrationId,
            });
          } catch (err) {
            // Money may well have left the account here, so never imply it did not.
            setPayError(
              `${err.message} Your payment reference is ${resp.razorpay_payment_id} — keep it, and our team will confirm your seat.`
            );
          } finally {
            setPaying(false);
          }
        },
      });

      rzp.on("payment.failed", (e) => {
        setPaying(false);
        setPayError(
          e?.error?.description
            ? `${e.error.description} Nothing has been charged — you can try again.`
            : "That payment did not go through. Nothing has been charged — you can try again."
        );
      });

      rzp.open();
    } catch (err) {
      setPaying(false);
      // The reference is what turns a support email into a fixable report.
      setPayError(err.reason ? `${err.message} (ref: ${err.reason})` : err.message);
    }
  };

  const onNameBlur = (k) => (e) => {
    if (NAME_FIELDS.includes(k)) set(k, titleCase(e.target.value.trim()));
  };

  if (handoff) {
    return (
      <div className={`rf rf--done${rail ? "" : " rf--norail"}`}>
        <div className="rf-done">
          <span className="rf-done__icon" aria-hidden="true"><Check strokeWidth={3} /></span>
          <h2 className="rf-done__title" tabIndex={-1} ref={headingRef}>You're registered</h2>
          <p className="rf-done__text">
            {handoff.students === 1 ? "Your registration" : `All ${handoff.students} registrations`} for{" "}
            {hackathon.eventName} 2026 {handoff.students === 1 ? "is" : "are"} confirmed and{" "}
            <b>{rupees(handoff.amount)}</b> {handoff.students === 1 ? "has" : "have"} been paid.
          </p>

          <div className="rf-done__paid">
            <p className="rf-done__paid-label">Payment received</p>
            <dl className="rf-done__ref">
              <div><dt>Registration ID</dt><dd>{String(handoff.registrationId).slice(-8).toUpperCase()}</dd></div>
              <div><dt>Payment ID</dt><dd>{handoff.paymentId}</dd></div>
            </dl>
            <p>
              A confirmation email is on its way to the {handoff.students === 1 ? "address" : "addresses"} you
              gave. Keep the payment ID — it is your proof of registration.
            </p>
          </div>

          <div className="rf-done__actions">
            {onClose && (
              <button type="button" className="rf-btn rf-btn--primary" onClick={onClose}>Close</button>
            )}
            <button
              type="button"
              className="rf-btn rf-btn--secondary"
              onClick={() => { clearDraft(); setF(EMPTY); setShown(new Set()); setHandoff(null); setPayError(""); go(0); }}
            >
              Register another team
            </button>
          </div>
        </div>
      </div>
    );
  }

  const s = STEPS[step];

  return (
    <div className={`rf${rail ? "" : " rf--norail"}`}>
      <div className="rf-main">
        <ol className="rf-steps">
          {STEPS.map((st, i) => {
            const done = i < step && stepValid(i, f);
            return (
              <li key={st.key} className={`rf-step${i === step ? " is-on" : ""}${done ? " is-done" : ""}`}>
                <button
                  type="button"
                  onClick={() => (i < step ? go(i) : undefined)}
                  disabled={i > step}
                  aria-current={i === step ? "step" : undefined}
                >
                  <span className="rf-step__n">{done ? <Check size={12} strokeWidth={3} /> : i + 1}</span>
                  <span className="rf-step__t">{st.title}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="rf-body" ref={bodyRef}>
          <h2 className="rf-heading" tabIndex={-1} ref={headingRef}>{s.heading}</h2>

          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={s.key}
              custom={dir}
              initial={(d) => ({ opacity: 0, x: d * 28 })}
              animate={{ opacity: 1, x: 0 }}
              exit={(d) => ({ opacity: 0, x: d * -28 })}
              transition={{ duration: 0.26, ease: EASE }}
            >
              {step === 0 && (
                <>
                  <div className="rf-modes">
                    {[
                      { id: "individual", Icon: User, title: "Individual", note: "Register yourself. You can join or form a team later." },
                      { id: "team", Icon: Users, title: `Team of ${MIN_TEAM}–${MAX_TEAM}`, note: "Register everyone together and pay once." },
                    ].map((m, i) => (
                      <button
                        key={m.id}
                        type="button"
                        className={`rf-mode${f.mode === m.id ? " is-on" : ""}`}
                        style={{ "--tone": i === 0 ? "#0041e8" : "#0b8a83", "--tint": i === 0 ? "#e8efff" : "#e2f4f1" }}
                        onClick={() => setMode(m.id)}
                        aria-pressed={f.mode === m.id}
                      >
                        <span className="rf-mode__icon" aria-hidden="true"><m.Icon strokeWidth={1.7} /></span>
                        <span className="rf-mode__title">{m.title}</span>
                        <span className="rf-mode__note">{m.note}</span>
                        <span className="rf-mode__price">
                          {m.id === "individual" ? reg.feeIndividualLabel : `${reg.feeTeamLabel} each · from ${rupees(registrationTotal("team", MIN_TEAM))}`}
                        </span>
                      </button>
                    ))}
                  </div>
                  {show("mode") && <p className="rf-field__err">{errors.mode}</p>}

                  {f.mode === "team" && (
                    <motion.div
                      className="rf-size"
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.22, ease: EASE }}
                    >
                      <div>
                        <p className="rf-size__label">How many students?</p>
                        <p className="rf-size__note">{eligibility.teamLabel}</p>
                      </div>
                      <div className="rf-stepper">
                        <button
                          type="button"
                          onClick={() => setSize(Math.max(MIN_TEAM, f.size - 1))}
                          disabled={f.size <= MIN_TEAM}
                          aria-label="One student fewer"
                        >
                          <Minus size={16} strokeWidth={2.6} />
                        </button>
                        <b aria-live="polite">{f.size}</b>
                        <button
                          type="button"
                          onClick={() => setSize(Math.min(MAX_TEAM, f.size + 1))}
                          disabled={f.size >= MAX_TEAM}
                          aria-label="One student more"
                        >
                          <Plus size={16} strokeWidth={2.6} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </>
              )}

              {step === 1 && (
                <div className="rf-grid">
                  <Field id="rf-school" label="School name" error={show("school")}>
                    <input
                      id="rf-school" type="text" value={f.school} placeholder="Full name of the school"
                      onChange={(e) => set("school", e.target.value)} onBlur={onNameBlur("school")}
                    />
                  </Field>
                  <Field id="rf-city" label="City" error={show("city")}>
                    <input
                      id="rf-city" type="text" value={f.city}
                      onChange={(e) => set("city", e.target.value)} onBlur={onNameBlur("city")}
                    />
                  </Field>
                  <Field id="rf-state" label="State / UT" error={show("state")}>
                    <select id="rf-state" value={f.state} onChange={(e) => set("state", e.target.value)}>
                      <option value="">Select</option>
                      {OPTIONS.states.map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                  </Field>
                  <Field id="rf-board" label="Board" error={show("board")}>
                    <select id="rf-board" value={f.board} onChange={(e) => set("board", e.target.value)}>
                      <option value="">Select</option>
                      {OPTIONS.boards.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div className="rf-members">
                  {f.mode === "team" && (
                    <Field id="rf-team" label="Team name" error={show("teamName")} hint="Anything you like — you can change it later.">
                      <input
                        id="rf-team" type="text" value={f.teamName} placeholder="e.g. Circuit Breakers"
                        onChange={(e) => set("teamName", e.target.value)} onBlur={onNameBlur("teamName")}
                      />
                    </Field>
                  )}

                  {f.members.slice(0, payingCount(f)).map((m, i) => {
                    const me = (touched && errors[`member${i}`]) || {};
                    // A typo like "gmial.com" is still a valid-looking address,
                    // so the suggestion cannot be gated on the format check.
                    const sug = emailSuggestion(m.email);
                    return (
                      <section key={i} className="rf-member" style={{ "--tone": ["#0041e8", "#0b8a83", "#6d3fe0", "#e05a12"][i % 4] }}>
                        <header className="rf-member__head">
                          <span className="rf-member__n">{i + 1}</span>
                          <h3>{i === 0 ? leadLabel(f) : `Member ${i + 1}`}</h3>
                          {i === 0 && f.mode === "team" && <span className="rf-member__tag">We contact this person</span>}
                        </header>

                        <div className="rf-grid">
                          <Field id={`rf-m${i}-name`} label="Full name" error={me.name}>
                            <input
                              id={`rf-m${i}-name`} type="text" value={m.name} placeholder="e.g. Aanya Sharma"
                              onChange={(e) => setMember(i, "name", e.target.value)}
                              onBlur={(e) => setMember(i, "name", titleCase(e.target.value.trim()))}
                            />
                          </Field>
                          <Field id={`rf-m${i}-class`} label="Class" error={me.studentClass}>
                            <ClassPicker
                              id={`rf-m${i}-class`} value={m.studentClass} error={me.studentClass}
                              onChange={(c) => setMember(i, "studentClass", c)}
                            />
                          </Field>
                          <Field id={`rf-m${i}-email`} label="Email" error={me.email}>
                            <input
                              id={`rf-m${i}-email`} type="email" value={m.email} inputMode="email" placeholder="name@example.com"
                              onChange={(e) => setMember(i, "email", e.target.value)}
                            />
                            {sug && (
                              <button type="button" className="rf-suggest" onClick={() => setMember(i, "email", sug)}>
                                Did you mean <b>{sug}</b>?
                              </button>
                            )}
                          </Field>
                          <Field
                            id={`rf-m${i}-phone`}
                            label="Mobile"
                            optional={i !== 0}
                            error={me.phone}
                          >
                            <input
                              id={`rf-m${i}-phone`} type="tel" inputMode="numeric" value={fmtPhone(m.phone)} placeholder="98765 43210"
                              onChange={(e) => setMember(i, "phone", phoneDigits(e.target.value))}
                            />
                          </Field>
                        </div>
                      </section>
                    );
                  })}

                  <section className="rf-extra">
                    <h3>{f.mode === "individual" ? "Parent or guardian" : "Mentor teacher"}</h3>
                    <div className="rf-grid">
                      {f.mode === "individual" ? (
                        <>
                          <Field id="rf-g-name" label="Guardian's name" error={show("guardianName")}>
                            <input
                              id="rf-g-name" type="text" value={f.guardianName}
                              onChange={(e) => set("guardianName", e.target.value)} onBlur={onNameBlur("guardianName")}
                            />
                          </Field>
                          <Field id="rf-g-phone" label="Guardian's mobile" optional error={show("guardianPhone")}>
                            <input
                              id="rf-g-phone" type="tel" inputMode="numeric" value={fmtPhone(f.guardianPhone)} placeholder="98765 43210"
                              onChange={(e) => set("guardianPhone", phoneDigits(e.target.value))}
                            />
                          </Field>
                        </>
                      ) : (
                        <>
                          <Field id="rf-mentor" label="Mentor teacher's name" optional error={show("mentorName")}
                            hint="A mentor teacher is not charged.">
                            <input
                              id="rf-mentor" type="text" value={f.mentorName}
                              onChange={(e) => set("mentorName", e.target.value)} onBlur={onNameBlur("mentorName")}
                            />
                          </Field>
                          <Field id="rf-mentor-phone" label="Mentor's mobile" optional error={show("mentorPhone")}>
                            <input
                              id="rf-mentor-phone" type="tel" inputMode="numeric" value={fmtPhone(f.mentorPhone)} placeholder="98765 43210"
                              onChange={(e) => set("mentorPhone", phoneDigits(e.target.value))}
                            />
                          </Field>
                        </>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {step === 3 && (
                <div className="rf-review">
                  <Summary f={f} compact />

                  <fieldset className="rf-pay">
                    <legend>Preferred payment method</legend>
                    <p className="rf-pay__hint">Choose how you'd like to pay. You can still switch method on the payment screen.</p>
                    <div className="rf-methods">
                      {reg.methods.map((mth) => (
                        <label key={mth} className={`rf-method${f.method === mth ? " is-on" : ""}`}>
                          <input
                            type="radio" name="rf-method" value={mth}
                            checked={f.method === mth} onChange={() => set("method", mth)}
                          />
                          <span>{mth}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <label className={`rf-consent${show("consent") ? " has-error" : ""}`}>
                    <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} />
                    <span>
                      I confirm a parent or guardian has agreed to {payingCount(f) === 1 ? "this" : "each"} student
                      taking part in {hackathon.eventName} 2026, and that the details above are correct.
                    </span>
                  </label>
                  {show("consent") && <p className="rf-field__err">{errors.consent}</p>}

                  <input
                    type="text" className="rf-hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
                    value={f.website} onChange={(e) => set("website", e.target.value)}
                  />
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        <footer className="rf-foot">
          {payError && (
            <p className="rf-foot__error" role="alert">{payError}</p>
          )}
          {f.mode && (
            <div className="rf-foot__total">
              <span>Total</span>
              <b>{rupees(registrationTotal(f.mode, payingCount(f)))}</b>
            </div>
          )}
          <div className="rf-foot__btns">
            {step > 0 && (
              <button type="button" className="rf-btn rf-btn--secondary" onClick={back}>Back</button>
            )}
            {step < LAST ? (
              <button type="button" className="rf-btn rf-btn--primary" onClick={next}>Continue</button>
            ) : (
              <button type="button" className="rf-btn rf-btn--primary" onClick={pay} disabled={paying}>
                {paying ? "Opening payment…" : `Pay ${rupees(registrationTotal(f.mode, payingCount(f)))}`}
              </button>
            )}
          </div>
        </footer>
      </div>

      {rail && <Summary f={f} />}
    </div>
  );
}
